import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Globe, CaretDown, CaretUp, ArrowCounterClockwise } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { WebsiteVisitorCard } from "../components/shared/WebsiteVisitorCard";
import { ConvertVisitorModal } from "../components/shared/ConvertVisitorModal";
import { ReassignVisitorModal } from "../components/shared/ReassignVisitorModal";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatET } from "../lib/dateUtils";

const navItems = [{ to: "/admin/website-visitors", label: "Website Visitors", icon: <Globe size={18} weight="duotone" />, testId: "nav-admin-website-visitors" }];

const TABS = [
  { key: "trust", label: "Trust Visitors", domain: "igchq.com" },
  { key: "funding", label: "Funding Visitors", domain: "irongatefunding.com" },
];

export default function AdminWebsiteVisitorsPage() {
  const [tab, setTab] = useState("trust");
  const [summary, setSummary] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [showSkipped, setShowSkipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);
  const [reassignTarget, setReassignTarget] = useState(null);

  const loadSummary = useCallback(() => {
    api.get("/admin/website-visitors/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);

  const loadVisitors = useCallback(() => {
    setLoading(true);
    api.get("/admin/website-visitors", { params: { category: tab, status: "pending_review" } })
      .then(({ data }) => setVisitors(data))
      .catch(() => toast.error("Couldn't load website visitors"))
      .finally(() => setLoading(false));
  }, [tab]);

  const loadSkipped = useCallback(() => {
    api.get("/admin/website-visitors/recently-skipped", { params: { category: tab } }).then(({ data }) => setSkipped(data)).catch(() => setSkipped([]));
  }, [tab]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadVisitors(); loadSkipped(); }, [loadVisitors, loadSkipped]);
  useEffect(() => { api.post("/admin/website-visitors/mark-viewed").then(loadSummary).catch(() => {}); }, [loadSummary]);

  const handleSkip = async (visitor) => {
    setBusyId(visitor.id);
    try {
      await api.post(`/admin/website-visitors/${visitor.id}/skip`);
      toast.success("Reviewed — skipped (undo within 24h below)");
      loadVisitors(); loadSkipped(); loadSummary();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const handleFlag = async (visitor) => {
    setBusyId(visitor.id);
    try {
      const { data } = await api.post(`/admin/website-visitors/${visitor.id}/flag`);
      toast.success(data.message);
      loadVisitors();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const handleUndoSkip = async (visitor) => {
    setBusyId(visitor.id);
    try {
      await api.post(`/admin/website-visitors/${visitor.id}/undo-skip`);
      toast.success("Restored to the queue");
      loadVisitors(); loadSkipped(); loadSummary();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const handleConverted = () => {
    setConvertTarget(null);
    loadVisitors();
    loadSummary();
  };

  const handleReassigned = () => {
    setReassignTarget(null);
    loadVisitors();
  };

  const pendingCount = (key) => summary?.[key]?.pending_count ?? 0;

  return (
    <DashboardLayout navItems={navItems} moduleName="Website Visitors" moduleBackPath="/admin">
      <div data-testid="admin-website-visitors-page">
        <div className="mb-6">
          <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-1">Website Visitors</h1>
          <p className="text-igc-muted text-sm">Organic site visitors captured by the DataMoon visitor webhooks — verified and scored. HIGH-confidence ones auto-assign to the next rep in rotation (reassign anytime below); MEDIUM waits here for your review.</p>
        </div>

        <div className="flex border-b border-igc-gold/20 mb-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`website-visitors-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              title={`Show organic visitors captured on ${t.domain}`}
              className={`py-2.5 px-4 text-sm font-medium flex items-center gap-2 ${tab === t.key ? "text-igc-goldText border-b-2 border-igc-gold" : "text-igc-muted hover:text-igc-cream"}`}
            >
              {t.label}
              <span className="text-[10px] px-1.5 py-0.5 bg-igc-gold/10 text-igc-gold" data-testid={`website-visitors-pending-badge-${t.key}`}>{pendingCount(t.key)}</span>
            </button>
          ))}
        </div>

        {loading && <p className="text-igc-muted text-sm" data-testid="website-visitors-loading">Loading...</p>}
        {!loading && visitors.length === 0 && (
          <p className="text-igc-muted text-sm bg-igc-surface border border-igc-gold/10 p-5" data-testid="website-visitors-empty-state">
            No pending visitors in the {TABS.find((t) => t.key === tab)?.domain} queue right now.
          </p>
        )}

        <div className="space-y-3">
          {visitors.map((v) => (
            <WebsiteVisitorCard key={v.id} visitor={v} busy={busyId === v.id} onConvert={setConvertTarget} onSkip={handleSkip} onFlag={handleFlag} onReassign={setReassignTarget} />
          ))}
        </div>

        <div className="mt-8">
          <button
            data-testid="website-visitors-recently-skipped-toggle"
            onClick={() => setShowSkipped((v) => !v)}
            title="Visitors you've skipped in the last 24 hours — undo any of them here"
            className="text-igc-muted text-xs uppercase tracking-widest flex items-center gap-1.5 hover:text-igc-cream transition-colors"
          >
            {showSkipped ? <CaretUp size={12} /> : <CaretDown size={12} />} Recently Skipped ({skipped.length}) — undo within 24h
          </button>
          {showSkipped && (
            <div className="mt-3 space-y-2" data-testid="website-visitors-recently-skipped-list">
              {skipped.length === 0 && <p className="text-igc-muted text-xs" data-testid="website-visitors-recently-skipped-empty">Nothing skipped in the last 24 hours.</p>}
              {skipped.map((v) => (
                <div key={v.id} className="bg-igc-surface border border-igc-gold/10 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`recently-skipped-row-${v.id}`}>
                  <div>
                    <p className="text-igc-cream text-sm">{v.name || "Unidentified Visitor"}</p>
                    <p className="text-igc-muted text-xs">Skipped {formatET(v.skipped_at, "MMM d, h:mm a")} ET</p>
                  </div>
                  <button
                    data-testid={`undo-skip-button-${v.id}`}
                    disabled={busyId === v.id}
                    onClick={() => handleUndoSkip(v)}
                    title="Restore this visitor to the pending queue"
                    className="text-xs px-3 py-1.5 border border-igc-gold/20 text-igc-muted hover:text-igc-goldText hover:border-igc-gold/40 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                  >
                    <ArrowCounterClockwise size={12} /> Undo
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {convertTarget && (
          <ConvertVisitorModal visitor={convertTarget} category={tab} onClose={() => setConvertTarget(null)} onConverted={handleConverted} />
        )}
        {reassignTarget && (
          <ReassignVisitorModal visitor={reassignTarget} onClose={() => setReassignTarget(null)} onReassigned={handleReassigned} />
        )}
      </div>
    </DashboardLayout>
  );
}
